import type { ChatAgentSession, Outcome } from '@litlingo/client';
import config from 'config';
// import { captureException } from '@sentry/react';
import type { Channel } from 'phoenix';
import { Socket } from 'phoenix';

export type BaseResponseData = {
  id: string;
};

export type AppendEventResponseData = BaseResponseData & {
  revision: number;
  state: ChatAgentSession;
  status: string;
};

export type WrappedSocketResponse<K extends BaseResponseData> = {
  type: string;
  payload: K;
};

export type Data = { outcomes: Outcome[]; in_flight: boolean; id: string };

let instance: DocumentsConnection | null = null;

export class DocumentsConnection {
  private _agentSessionId = '';

  private _authSessionId = '';

  private _socket: Socket | null = null;

  private _agentChannel: Channel | null = null;

  private _authChannel: Channel | null = null;

  private _token: string;

  private _customerId: string;

  private _displayLanguage: string;

  constructor(token: string, customerId: string, displayLanguage = 'en-US') {
    this._token = '';
    this._displayLanguage = displayLanguage;
    this._customerId = customerId;
    this._token = token;

    if (instance && instance._customerId === customerId) {
      return instance;
    }

    instance = this;
    return this;
  }

  isConnected = (): boolean => this._socket?.isConnected() || false;

  isConnecting = (): boolean => this._socket?.connectionState() === 'connecting' || false;

  isAuthChannelConnected = (): boolean => this._authChannel?.state === 'joined' || false;

  isAgentChannelConnected = (): boolean => this._agentChannel?.state === 'joined' || false;

  getAgentSessionId = (): string => this._agentSessionId;

  getAuthSessionId = (): string => this._authSessionId;

  getCustomerId = (): string => this._customerId;

  connect = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const socket = new Socket(config.documents.socket, {
        params: {
          token: this._token,
          displayLanguage: this._displayLanguage,
          customerId: this._customerId,
        },
      });

      this._socket = socket;

      this._socket.onOpen(() => {
        resolve();
      });

      this._socket.onClose(() => {
        resolve();
      });

      this._socket.onError((error) => {
        this._socket?.disconnect(() => {
          reject(new Error(error.toString() || 'Socket failed to connect'));
        });
      });

      this._socket.connect();
    });

  joinAgentSession = (id: string): Promise<ChatAgentSession> =>
    new Promise((resolve, reject) => {
      if (!this._socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      this._agentSessionId = id;
      const channel = this._socket.channel(`agent_session:${this._agentSessionId}`, {});
      this._agentChannel = channel;

      this._agentChannel
        .join()
        .receive('ok', (res) => {
          resolve(res);
        })
        .receive('error', (res) => {
          reject(new Error('Socket failed to connect to agent channel', { cause: res }));
        })
        .receive('timeout', (res) => {
          reject(res);
        });
    });

  joinAuthSession = (id: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!this._socket) {
        reject(new Error('Socket not connected auth'));
        return;
      }

      this._authSessionId = id;
      const channel = this._socket.channel(`webapp_session:${this._authSessionId}`, {});
      this._authChannel = channel;

      this._authChannel.join().receive('ok', (res) => {
        resolve(res);
      });
    });

  sendMessage = <D extends BaseResponseData>(
    message: string,
    payload: Record<string, unknown>,
    channel: 'agent' | 'auth',
    receiveCallback?: (res: D) => void,
    errorCallback?: (error: unknown) => void
  ): Promise<D | null> =>
    new Promise<D | null>((resolve, reject) => {
      const socketChannel = channel === 'agent' ? this._agentChannel : this._authChannel;
      if (!socketChannel) {
        reject(new Error('Channel not connected'));
        return;
      }

      socketChannel
        .push(message, payload)
        .receive('ok', (res) => {
          if (receiveCallback) {
            receiveCallback(res);
          }
          resolve(res);
        })
        .receive('error', (error) => {
          if (errorCallback) {
            errorCallback(error);
            reject(new Error(error.reason || 'Failed to send message'));
          }
          // captureException(new SocketError(error.message), {
          //   extra: { ...error },
          // });
        });
    });

  sendMessageAgentChannel = <D extends BaseResponseData>(
    message: string,
    payload: Record<string, unknown>,
    receiveCallback?: (res: D) => void,
    errorCallback?: (error: unknown) => void
  ): Promise<D | null> =>
    this.sendMessage(message, payload, 'agent', receiveCallback, errorCallback);

  sendMessageAuthChannel = <D extends BaseResponseData>(
    message: string,
    payload: Record<string, unknown>,
    receiveCallback?: (res: D) => void,
    errorCallback?: (error: unknown) => void
  ): Promise<D | null> =>
    this.sendMessage(message, payload, 'auth', receiveCallback, errorCallback);

  appendEvent = (
    payload: {
      type: string;
      role: string;
      content: string;
      metadata: Record<string, string>;
    },
    receiveCallback?: (res: AppendEventResponseData) => void,
    errorCallback?: (res: unknown) => void
  ): Promise<AppendEventResponseData | null> =>
    this.sendMessageAgentChannel<AppendEventResponseData>(
      'append_event',
      { id: this._agentSessionId, event: payload },
      receiveCallback,
      errorCallback
    );

  registerOnMessageReceived = (
    message: string,
    channel: 'agent' | 'auth',
    callback: (response: AppendEventResponseData | null) => void
  ): void => {
    const socketChannel = channel === 'agent' ? this._agentChannel : this._authChannel;
    if (!socketChannel) {
      throw new Error('Channel not connected');
    }

    socketChannel.on(message, callback);
  };
}

async function initWebsocketClient(
  token: string,
  customerId: string,
  displayLanguage?: string
): Promise<DocumentsConnection> {
  if (instance && instance.getCustomerId() === customerId) {
    return instance;
  }

  const connection = new DocumentsConnection(token, customerId, displayLanguage);
  await connection.connect();

  return connection;
}

export async function joinAgentChannel(
  id: string,
  token: string,
  customerId: string,
  displayLanguage?: string
): Promise<{ response: ChatAgentSession | null; connection: DocumentsConnection }> {
  const connection = await initWebsocketClient(token, customerId, displayLanguage);
  if (connection.isAgentChannelConnected() && connection.getAgentSessionId().includes(id)) {
    return { response: null, connection };
  }
  const response = await connection.joinAgentSession(id);

  return { response, connection };
}

export async function joinAuthChannel(
  id: string,
  token: string,
  customerId: string,
  displayLanguage?: string
): Promise<DocumentsConnection> {
  const connection = await initWebsocketClient(token, customerId, displayLanguage);
  if (connection.isAuthChannelConnected() && connection.getAuthSessionId().includes(id)) {
    return connection;
  }

  await connection.joinAuthSession(id);

  return connection;
}

export function getWebsocketClient(): DocumentsConnection | null {
  if (instance) {
    return instance;
  }

  return null;
}

export default initWebsocketClient;
